// Airspace from OpenAIP, fetched through the Worker per map tile (the Worker holds the API key
// and caches each tile).

import { lngLatToTile, type LngLat } from '../lib/geo';
import type { Airspace, AirspaceLimit, AirspaceSource, LimitRef, LimitUnit } from './types';

// OpenAIP enumerations (https://docs.openaip.net). Only the labels we display.
const TYPES: Record<number, string> = {
  0: 'Other', 1: 'R', 2: 'D', 3: 'P', 4: 'CTR', 5: 'TMZ', 6: 'RMZ', 7: 'TMA', 8: 'TRA', 9: 'TSA',
  10: 'FIR', 11: 'UIR', 12: 'ADIZ', 13: 'ATZ', 14: 'MATZ', 15: 'AWY', 16: 'MTR', 17: 'Alert',
  18: 'Warning', 19: 'Protected', 20: 'HTZ', 21: 'Gliding', 22: 'TRP', 23: 'TIZ', 24: 'TIA',
  25: 'MTA', 26: 'CTA', 27: 'ACC', 28: 'Sport', 29: 'LowFlight', 30: 'MRT', 31: 'TFR',
  32: 'VFR', 33: 'FIS', 34: 'LTA', 35: 'UTA',
};
const CLASSES: Record<number, string> = { 0: 'A', 1: 'B', 2: 'C', 3: 'D', 4: 'E', 5: 'F', 6: 'G' };
/** Region-wide information sectors that cover everything; not useful for conflict checks. */
const IGNORED_TYPES = new Set([10, 11, 27, 33]);

interface RawLimit {
  value: number;
  unit: number;
  referenceDatum: number;
}
export interface RawAirspace {
  _id: string;
  name: string;
  type: number;
  icaoClass?: number;
  geometry: { type: string; coordinates: unknown };
  lowerLimit: RawLimit;
  upperLimit: RawLimit;
}

function limit(l: RawLimit): AirspaceLimit {
  const unit: LimitUnit = l.unit === 6 ? 'FL' : l.unit === 1 ? 'ft' : 'm';
  const ref: LimitRef = l.referenceDatum === 0 ? 'GND' : l.referenceDatum === 2 ? 'STD' : 'MSL';
  return { value: l.value, unit, ref };
}

/** Map raw OpenAIP items to `Airspace`, expanding MultiPolygons. Exported for tests. */
export function mapOpenAip(items: RawAirspace[]): Airspace[] {
  const out: Airspace[] = [];
  for (const a of items) {
    if (IGNORED_TYPES.has(a.type)) continue;
    const base = {
      name: a.name,
      type: TYPES[a.type] ?? 'Other',
      icaoClass: a.icaoClass === undefined ? undefined : CLASSES[a.icaoClass],
      floor: limit(a.lowerLimit),
      ceiling: limit(a.upperLimit),
    };
    if (a.geometry.type === 'Polygon') {
      out.push({ id: a._id, ...base, polygon: a.geometry.coordinates as LngLat[][] });
    } else if (a.geometry.type === 'MultiPolygon') {
      (a.geometry.coordinates as LngLat[][][]).forEach((poly, i) => out.push({ id: `${a._id}#${i}`, ...base, polygon: poly }));
    }
  }
  return out;
}

export class OpenAipSource implements AirspaceSource {
  readonly id = 'openaip';
  readonly attribution = 'Airspace: <a href="https://www.openaip.net/">OpenAIP</a> (CC BY-NC 4.0)';
  private cache = new Map<string, Promise<RawAirspace[]>>();

  constructor(private apiBase: string, private zoom: number) {}

  private tile(x: number, y: number): Promise<RawAirspace[]> {
    const key = `${this.zoom}/${x}/${y}`;
    let p = this.cache.get(key);
    if (!p) {
      p = fetch(`${this.apiBase}/airspace/${key}`).then(async (r) => {
        if (!r.ok) throw new Error(`Airspace unavailable (${r.status})`);
        return ((await r.json()) as { items: RawAirspace[] }).items;
      });
      p.catch(() => this.cache.delete(key));
      this.cache.set(key, p);
    }
    return p;
  }

  async query(sw: LngLat, ne: LngLat): Promise<Airspace[]> {
    const a = lngLatToTile([sw[0], ne[1]], this.zoom);
    const b = lngLatToTile([ne[0], sw[1]], this.zoom);
    const jobs: Promise<RawAirspace[]>[] = [];
    for (let y = Math.floor(a.y); y <= Math.floor(b.y); y++)
      for (let x = Math.floor(a.x); x <= Math.floor(b.x); x++) jobs.push(this.tile(x, y));
    const seen = new Set<string>();
    const items: RawAirspace[] = [];
    for (const list of await Promise.all(jobs))
      for (const it of list)
        if (!seen.has(it._id)) {
          seen.add(it._id);
          items.push(it);
        }
    return mapOpenAip(items);
  }
}
